import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject, filter, take } from 'rxjs';
import { UpdatePropertyStatusDialogComponent } from '../properties-table/components/update-property-status-dialog/update-property-status-dialog.component';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { DetailPair } from '../../../shared/components/detail-info-card/DetailPair';
import { CountryService } from '../../../shared/reference/country.service';
import { OrganizationService } from '../../organizations/services/organizations.service';
import { AssignPolicyDialogComponent } from '../../policies/components/assign-policy-dialog/assign-policy-dialog.component';
import { ClearPrimaryPolicyDialogComponent } from '../../policies/components/clear-primary-policy-dialog/clear-primary-policy-dialog.component';
import { DeletePolicyDialogComponent } from '../../policies/components/delete-policy-dialog/delete-policy-dialog.component';
import { PolicyRow, Policy, PolicyPrice, PropertyPolicyColumns } from '../../policies/models/Policy';
import { PolicyService } from '../../policies/services/policy.service';
import { PriceRangeModalComponent } from '../../price-rates/components/price-range-modal/price-range-modal.component';
import { PriceRatesService } from '../../price-rates/services/price-rates.service';
import { DetailInfoCardComponent } from '../../../shared/components/detail-info-card/detail-info-card.component';
import { SharedPolicyTableComponent } from '../../policies/components/shared-policy-table/shared-policy-table/shared-policy-table.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { PropertyApiService } from '../services/property.api.service';
import { SafelyResource } from '../../../shared/SafelyResource';
import { PropertyReservationsComponent } from "./components/property-reservations/property-reservations.component";
import { PropertyClaimsComponent } from "./components/property-claims/property-claims.component";

@Component({
    selector: 'app-properties-detail',
    templateUrl: './properties-detail.component.html',
    styleUrls: ['./properties-detail.component.scss'],
    standalone: true,
    imports: [
    CommonModule,
    MatIcon,
    MatCard,
    MatTabsModule,
    DetailInfoCardComponent,
    MatButtonModule,
    MatProgressSpinner,
    BackButtonComponent,
    SharedPolicyTableComponent,
    PropertyReservationsComponent,
    PropertyClaimsComponent
],
    providers: [ CurrencyPipe ]
})
export class PropertiesDetailComponent implements OnInit {

    @Input() propertyId!: number;
    @Input() disableActions: boolean = false;
    @ViewChild(PropertyReservationsComponent) private propertyReservationsComponent!: PropertyReservationsComponent;
    @ViewChild(PropertyClaimsComponent) private propertyClaimsComponent!: PropertyClaimsComponent;

    basicInfo: DetailPair[] = [];
    description!: DetailPair;
    address: DetailPair[] = [];
    homeowner: DetailPair[] = [];
    photos: string[] = [];
    property!: any;

    loading = new BehaviorSubject<boolean>(false);
    keyword!: string;
    pageSize = 10;
    showPaginator = false;
    policyTableLength = 0;
    policyColumns = PropertyPolicyColumns;
    tabIndex: number = 0;
    policyDataSource = new MatTableDataSource<PolicyRow>();

    propertyPolicies: Policy[] = [];
    organizationPolicies: Policy[] = [];
    availablePolicies: PolicyRow[] = [];
    availableOrganizationPolicies: Policy[] = [];
    availablePriceRates: PolicyPrice[] = [];
    selectedPolicy?: Policy;
    policyToUpdate?: Policy;
    policiesAvailableBoolean: boolean = true;
    displayLoadingIndicator: boolean = false;
    propertyHasPrimaryPolicy: boolean = false;

    constructor(
        private propertiesService: PropertyApiService,
        private route: ActivatedRoute,
        private policyService: PolicyService,
        private priceRatesService: PriceRatesService,
        private dialog: MatDialog,
        private router: Router,
        private countryService: CountryService,
        private logService: Logger,
        private userContextProvider: UserContextProvider,
        private organizationService: OrganizationService
    ) { }

    ngOnInit(): void {
        this.userContextProvider.isContextLoaded$.pipe(
            filter(loaded => loaded),
            take(1)
        ).subscribe(() => {
            this.route.params.subscribe(params => {
                if (this.propertyId == null || this.propertyId === 0) {
                    this.propertyId = params['id'];
                }
                this.loadPage(this.propertyId);
            });
        });

        this.loading.subscribe(loading => {
            this.displayLoadingIndicator = loading;
        });
    }

    loadPage(propertyId: number) {
        try {
            this.loading.next(true);
            this.findPropertyById(propertyId);
            this.findAvailablePriceRates();
        } catch (e) {
            this.logService.handleError(e, 'An error occurred while retrieving Property details', 'Error while retrieving Property details');
        } finally {
            this.loading.next(false);
        }
    }

    async findPropertyById(id: number) {
        if (!id) {
            return;
        }

        try {
            this.property = await this.propertiesService.getPropertyDetail(id);
            if (!this.property) {
                this.logService.handleError(`An error occured retrieving Property details for ID: ${id}`, `An error occured retrieving Property details`, `Error while retrieving Property details for ID: ${id}`);
                return;
            } else {
                this.populateBasicInfo();
                this.populateAddress();
                this.populateHomeowner();
                this.populatePhotos();
                this.findPropertyPoliciesById(this.propertyId);
            }
        } catch (e) {
            this.logService.handleError(e, 'An error occurred while retrieving Property details', 'Error while retrieving Property details');
        }
    }

    async findPropertyPoliciesById(propertyId: any) {
        const propertyPolicies = await this.propertiesService.getPropertyPolicies(propertyId);
        this.propertyPolicies = propertyPolicies.filter(policy => policy.status_id === 1);

        if (this.propertyPolicies.some(policy => policy.is_primary === 1)) {
            this.propertyHasPrimaryPolicy = true;
        } else {
            this.propertyHasPrimaryPolicy = false;
        }

        this.populatePropertyPolicies();
        this.getOrganizationPolicies(this.property.organization_id);
    }

    async getOrganizationPolicies(organizationId: number) {
        this.availablePolicies = [];
        this.availableOrganizationPolicies = [];

        const organizationPolicyResponse: any[] = await this.organizationService.getPoliciesByOrganizationId(organizationId);
        let availableOrgPolicies: any[] = organizationPolicyResponse.filter(policy => policy.status_id === 1);

        let availablePolicies: any[] = [];
        availableOrgPolicies.forEach(orgPolicy => {
            availablePolicies.push(orgPolicy.policy);
        });
        this.availableOrganizationPolicies = availablePolicies;

        this.availablePolicies = availablePolicies.filter(orgPolicy => !this.propertyPolicies.find(policy => orgPolicy.id === policy.insurance_policy_id));
        if (this.availablePolicies.length === 0) {
            this.policiesAvailableBoolean = false;
        }
    }

    async findAvailablePriceRates() {
        this.availablePriceRates = [];
        this.availablePriceRates = await this.priceRatesService.getPriceRates();
    }

    async populatePropertyPolicies() {
        if (this.propertyPolicies) {
            this.policyDataSource.data = this.propertyPolicies.map((policy: Policy) => this.policyService.getPolicyRowFromPolicy(policy));
            this.policyTableLength = this.policyDataSource.data.length;
        }
    }

    populateBasicInfo() {
        if (this.property) {
            this.basicInfo = [];

            const organizationIdOptions = {
                id: this.property.organization_id,
                resource: SafelyResource.PROPERTY,
                routeId: this.property.organization_id,
                route: `/organizations/detail/${this.property.organization_id}`
            }

            this.basicInfo.push(new DetailPair("Safely Prop ID", this.property.property_id));
            this.basicInfo.push(new DetailPair("Property ID", this.property.property_reference_id ? this.property.property_reference_id : 'Not Provided'));
            this.basicInfo.push(new DetailPair("Organization ID", this.property.organization_id, '', organizationIdOptions, true));
            this.basicInfo.push(new DetailPair("Organization Name", this.property.organization_name, '', organizationIdOptions, true));
            this.basicInfo.push(new DetailPair("Property Type", this.getPropertyTypeFromId(this.property.property_type)));
            this.basicInfo.push(new DetailPair("Status", this.property.status.display_text));
            this.basicInfo.push(new DetailPair("Accommodates", this.property.accommodates));
            this.basicInfo.push(new DetailPair("Bedrooms", this.property.bedrooms));
            this.basicInfo.push(new DetailPair("Bathrooms", this.property.bathrooms));
            this.basicInfo.push(new DetailPair("Description", this.property.description));
        }
    }

    async populateAddress() {
        this.address = [];

        const address = this.property.address;
        const country = await this.countryService.getCountryFromId(address.country_id);

        this.address.push(new DetailPair("Address Line 1", address.address_line_1));
        this.address.push(new DetailPair("Address Line 2", address.address_line_2));
        this.address.push(new DetailPair("City/Town", address.city));
        this.address.push(new DetailPair("State/Province", address.state));
        this.address.push(new DetailPair("Postal Code", address.postal_code));
        this.address.push(new DetailPair("Country", country?.display_text || ''));
    }

    async populateHomeowner() {
        const address = this.property.property_owner_address;
        const country = await this.countryService.getCountryFromId(address.country_id);

        this.homeowner = [];
        this.homeowner.push(new DetailPair("Full Name", this.property.property_owner_name));
        this.homeowner.push(new DetailPair("Email Address", this.property.property_owner_email));
        this.homeowner.push(new DetailPair("Phone Number", this.property.property_owner_phone));
        this.homeowner.push(new DetailPair("Address Line 1", address.address_line_1));
        this.homeowner.push(new DetailPair("Address Line 2", address.address_line_2));
        this.homeowner.push(new DetailPair("City/Town", address.city));
        this.homeowner.push(new DetailPair("State/Province", address.state));
        this.homeowner.push(new DetailPair("Postal Code", address.postal_code));
        this.homeowner.push(new DetailPair("Country", country?.display_text || ''));
    }

    populatePhotos() {
        this.property.photos?.forEach((photo: any) => this.photos.push(photo.url));
    }

    couldNotLoadPhoto(photo: string) {
        this.logService.handleError(`Error loading photo: ${photo}`, `Error loading photo: ${photo}`, `Error loading photo: ${photo}`);
        this.photos.splice(this.photos.indexOf(photo), 1);
    }

    onEditPropertyClick() {
        this.router.navigate(['/properties/form'], { queryParams: { propertyId: this.propertyId }});
    }

    getPolicyPrimaryColorForTable(primaryflag: string): string {
        if (primaryflag === "Primary") {
            return '#5cb85c';
        } else {
            return '#d3d3d3';
        }
    }

    onViewRangesOpenClick(row: any) {
        this.dialog.open(PriceRangeModalComponent, {
            width: '70%',
            data: { row }
        });
    }

    tabChanged(tabChangeEvent: MatTabChangeEvent) {
        this.tabIndex = tabChangeEvent.index;
        const selectedTabLabel = tabChangeEvent.tab.textLabel;
        if (selectedTabLabel === 'Property Reservations') {
            this.emitLoadPropertyReservations();
        }
        if (selectedTabLabel === 'Property Claims') {
            this.emitLoadPropertyClaims();
        }
    }

    emitLoadPropertyClaims() {
        this.propertyClaimsComponent.loadPropertyClaimsTab();
    }

    emitLoadPropertyReservations() {
        this.propertyReservationsComponent.loadPropertyReservationsTab();
    }

    getPropertyTypeFromId = (typeId: number): string => {
        switch (typeId) {
            case 1:
                return "Apartment";
            case 2:
                return "Townhouse";
            case 3:
                return "Single Family House";
            case 4:
                return "Hotel";
            case 5:
                return "Others";
            default:
                return "";
        }
    }

    onEditPolicyClick(row: any) {
        this.selectedPolicy = row;
        this.policyToUpdate = this.propertyPolicies.find(policy => policy.id === row.id);
        if (this.policyToUpdate) {
            this.policyToUpdate.policy_price_id = row.policyPriceId;
            this.onAssignPolicyClick(this.policyToUpdate);
        }
    }

    onAssignPolicyClick(policyToUpdate?: Policy) {
        const assignDialogRef = this.dialog.open(AssignPolicyDialogComponent, {
            minWidth: '70%',
            maxHeight: '850px',
            disableClose: true,
            autoFocus: false,
            data: {
                component: 'properties-detail',
                propertyId: this.propertyId,
                availablePolicies: this.availablePolicies,
                availablePriceRates: this.availablePriceRates,
                policyToUpdate: policyToUpdate ? policyToUpdate : []
            }
        });
        assignDialogRef.afterClosed().subscribe(data => {
            if (data) {
                try {
                    if (this.selectedPolicy) {
                        const propertyUpdatePolicyPayload = this.buildUpdatePropertyPolicy(data, this.selectedPolicy);
                        this.policyService.updatePolicyByComponent(this.selectedPolicy.id, propertyUpdatePolicyPayload, 'properties').then(_ => {
                            this.findPropertyPoliciesById(this.propertyId);
                        });
                    } else {
                        const propertyPolicyPayload = this.buildPropertyPolicy(data);
                        this.policyService.createPolicyByComponent(propertyPolicyPayload, 'properties').then(_ => {
                            this.findPropertyPoliciesById(this.propertyId);
                        });
                    }
                } catch (e) {
                    this.logService.handleError(e, 'An error occurred while submitting Policy', 'Error while submitting Policy');
                }
            }
            this.policyToUpdate = undefined;
        });
    }

    buildPropertyPolicy(data: any) {
        let payload = {
            insurance_policy_id: data.policy.id.toString(),
            organization_id: this.property.organization_id.toString(),
            policy_price_id: data.policyPrice,
            property_id: Number(data.propertyId),
            status_id: 1
        }
        return payload;
    }

    buildUpdatePropertyPolicy(data: any, selectedPolicy: any) {
        let isPrimary: number = 0;
        if (selectedPolicy.pillStatus === "Primary") {
            isPrimary = 1;
        }

        let payload = {
            account_id: this.selectedPolicy?.account_id,
            id: selectedPolicy.id,
            insurance_policy_id: data.policy.id.toString(),
            is_active: 1,
            is_primary: isPrimary,
            keep_history: 1,
            organization_id: this.property.organization_id.toString(),
            original_id: null,
            policy_price_id: data.policyPrice,
            property_id: Number(data.propertyId),
            status_id: 1
        }
        return payload;
    }

    onUpdatePrimaryPolicyClick(row: any) {
        if (row.pillStatus === 'Primary') {
            this.openUpdatePrimaryPolicyDialog(row.id, false);
        } else {
            this.openUpdatePrimaryPolicyDialog(row.id, true);
        }
    }

    openUpdatePrimaryPolicyDialog(policyId: number, setAsPrimaryPolicy: boolean) {
        const updateDialogRef = this.dialog.open(ClearPrimaryPolicyDialogComponent, {
            width: '60%',
            disableClose: true,
            data: {
                component: 'Policy',
                setAsPrimaryPolicy: setAsPrimaryPolicy
            }
        });
        updateDialogRef.afterClosed().subscribe((setPrimary: any) => {
            if (setPrimary != 'cancel') {
                let payload: any = {
                    id: policyId,
                    is_primary: setPrimary,
                    keep_history: 0
                }
                this.policyService.updatePolicyByComponent(policyId, payload, 'properties').then(_ => {
                    this.findPropertyPoliciesById(this.propertyId);
                });
            }
        });
    }

    onDeletePolicyClick(row: Policy) {
        this.openDeletePolicyDialog(row);
    }

    openDeletePolicyDialog(policy: any) {
        let primaryPolicyBoolean: boolean = false;
        if (policy.pillStatus === "Primary") {
            primaryPolicyBoolean = true;
        }
        
        const deleteDialogRef = this.dialog.open(DeletePolicyDialogComponent, {
            width: '60%',
            disableClose: true,
            data: {
                component: 'Policy',
                propertyPolicyExists: false,
                primaryPolicy: primaryPolicyBoolean
            }
        });
        deleteDialogRef.afterClosed().subscribe((data: boolean) => {
            if (data) {
                let payload: any = {
                    id: policy.id,
                    is_primary: 0,
                    is_active: 0,
                    keep_history: 0
                }
                this.policyService.deletePolicyByComponent(policy.id, payload, 'properties').then(_ => {
                    this.findPropertyPoliciesById(this.propertyId);
                    this.policiesAvailableBoolean = true;
                });
            }
        });
    }

    openUpdatePropertyStatusDialog() {
        let propertyToUpdate = [];
        propertyToUpdate.push(this.property);

        const statusDialogRef = this.dialog.open(UpdatePropertyStatusDialogComponent, {
          width: '750px',
          maxHeight: '600px',
          disableClose: true,
          data: propertyToUpdate
        });
        statusDialogRef.afterClosed().subscribe((statusId: number) => {
          if (statusId > 0) {
            try {
                this.property.status_id = statusId;
                this.propertiesService.updatePropertyStatus(this.property).then(_ => {
                    this.router.navigate(['/properties']);
                });
            } catch (e) {
                this.logService.handleError(e, 'An error occurred while updating Property status', 'Error while updating Property status');
            }
          }
        });
    }
}